import React from 'react';
import PropTypes, { InferProps } from 'prop-types';
import styled from '@emotion/styled';
import { Grid, GridCell } from './Grid';
import {
  JobTitleIcon,
  JobCompanyIcon,
  JobLocationIcon,
  JobDatePostedIcon,
  JobCountryIcon,
  JobCategoryIcon,
  JobHowToApplyIcon,
} from './Icons/JobIcons';
import Subtitle from './Typography/SubTitle';
import breakpoints from './Theme/breakpoints';
import getAddressLine2 from './lib/getAddressLine2';
import colors from './Theme/colors';

const JobInfoGrid = styled(Grid)`
  font-family: ${({ theme }) => theme.fonts.primary};
  &&& {
    padding: 32px 16px 0;
    border-top: 1px solid ${({ theme }) => theme.colors.greyLight};

    @media (min-width: ${breakpoints.sm}px) {
      padding: 32px 24px 0;
    }
  }
`;

const DetailsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const StyledSubtitle = styled(Subtitle)`
line-height: ${
  (properties) => properties.theme.typography?.subtitle2?.lineHeight
};
  &&& {
    font-family: ${({ theme }) => theme.fonts.primary};
  }
`;

const DetailTitle = styled.div`
  width: 50%;
  display: flex;
  align-items: center;

  @media (min-width: ${breakpoints.sm}px) {
    width: 30%;
  }

  @media (min-width: ${breakpoints.lg}px) {
    width: 40%;
  }

  svg {
    margin-right: 12px;
  }
`;

const DetailData = styled.div`
  width: 40%;
  padding-left: 16px;
  overflow-wrap: break-word;

  span {
    font-weight: bold;
    color: ${({ theme }) => theme.colors.blackHigh};
  }

  @media (min-width: ${breakpoints.md}px) {
    width: 50%;
  }
`;

const CompanyDetailData = styled(DetailData)`
  a {
    color: ${({ theme }) => theme.colors.primaryText};
  }
  a:hover, a:active {
    text-decoration: underline;
  }
`;

const locationDataPropertyTypes = {
  buildingName: PropTypes.string,
  address: PropTypes.string,
  city: PropTypes.string.isRequired,
  addressRegion: PropTypes.string,
  postalCode: PropTypes.string,
  isAmericanFormat: PropTypes.bool.isRequired,
};

type LocationDataProperties = InferProps<
  typeof locationDataPropertyTypes
>;

const LocationData = ({
  buildingName,
  address,
  city,
  postalCode,
  addressRegion,
  isAmericanFormat,
}: LocationDataProperties) => (
  <>
    { buildingName && (<div>{ buildingName }</div>) }
    { address && (<div>{ address }</div>) }
    <div>
      {getAddressLine2(isAmericanFormat, city, postalCode, addressRegion)}
    </div>
  </>
);

const jobInfoTablePropertyTypes = {
  titleLabel: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  categoryLabel: PropTypes.string.isRequired,
  category: PropTypes.string.isRequired,
  companyNameLabel: PropTypes.string.isRequired,
  companyName: PropTypes.node.isRequired,
  regionLabel: PropTypes.string.isRequired,
  country: PropTypes.string.isRequired,
  datePostLabel: PropTypes.string.isRequired,
  datePost: PropTypes.node.isRequired,
  howToApplyLabel: PropTypes.string.isRequired,
  howToApply: PropTypes.string.isRequired,
  locationLabel: PropTypes.string.isRequired,
  buildingName: PropTypes.string,
  address: PropTypes.string,
  city: PropTypes.string.isRequired,
  addressRegion: PropTypes.string,
  postalCode: PropTypes.string,
};

type JobInfoTableProperties = InferProps<
  typeof jobInfoTablePropertyTypes
>;

const JobInfoTable = ({
  titleLabel,
  title,
  categoryLabel,
  category,
  companyNameLabel,
  companyName,
  regionLabel,
  country,
  datePostLabel,
  datePost,
  howToApplyLabel,
  howToApply,
  locationLabel,
  buildingName,
  address,
  city,
  addressRegion,
  postalCode,
}: JobInfoTableProperties) => (
  <JobInfoGrid spacing={0}>
    <GridCell xs={12} lg={6}>
      <DetailsContainer>
        <DetailTitle>
          <JobTitleIcon
            fill={colors.blackHigh}
            title={titleLabel}
          />
          <StyledSubtitle>
            {titleLabel}
            {': '}
          </StyledSubtitle>
        </DetailTitle>
        <DetailData>
          <StyledSubtitle>
            {title}
          </StyledSubtitle>
        </DetailData>
      </DetailsContainer>
      <DetailsContainer>
        <DetailTitle>
          <JobCompanyIcon
            fill={colors.blackHigh}
            title={companyNameLabel}
          />
          <StyledSubtitle>
            {companyNameLabel}
            {': '}
          </StyledSubtitle>
        </DetailTitle>
        <CompanyDetailData>
          <StyledSubtitle>
            {companyName}
          </StyledSubtitle>
        </CompanyDetailData>
      </DetailsContainer>
      <DetailsContainer>
        <DetailTitle>
          <JobCategoryIcon
            fill={colors.blackHigh}
            title={categoryLabel}
          />
          <StyledSubtitle>
            {categoryLabel}
            {': '}
          </StyledSubtitle>
        </DetailTitle>
        <DetailData>
          <StyledSubtitle>
            {category}
          </StyledSubtitle>
        </DetailData>
      </DetailsContainer>
      <DetailsContainer>
        <DetailTitle>
          <JobDatePostedIcon
            fill={colors.blackHigh}
            title={datePostLabel}
          />
          <StyledSubtitle>
            {datePostLabel}
            {': '}
          </StyledSubtitle>
        </DetailTitle>
        <DetailData>
          <StyledSubtitle>
            {datePost}
          </StyledSubtitle>
        </DetailData>
      </DetailsContainer>
    </GridCell>
    <GridCell xs={12} lg={6}>
      <DetailsContainer>
        <DetailTitle>
          <JobLocationIcon
            fill={colors.blackHigh}
            title={locationLabel}
          />
          <StyledSubtitle>
            {locationLabel}
            {': '}
          </StyledSubtitle>
        </DetailTitle>
        <DetailData>
          <StyledSubtitle>
            <LocationData
              buildingName={buildingName}
              address={address}
              city={city}
              addressRegion={addressRegion}
              postalCode={postalCode}
              isAmericanFormat={[
                'United States of America',
                'Canada',
              ].includes(country)}
            />
          </StyledSubtitle>
        </DetailData>
      </DetailsContainer>
      <DetailsContainer>
        <DetailTitle>
          <JobCountryIcon
            fill={colors.blackHigh}
            title={regionLabel}
          />
          <StyledSubtitle>
            {regionLabel}
            {': '}
          </StyledSubtitle>
        </DetailTitle>
        <DetailData>
          <StyledSubtitle>
            {addressRegion}
            {(addressRegion && country) && (', ')}
            {country}
          </StyledSubtitle>
        </DetailData>
      </DetailsContainer>
      <DetailsContainer>
        <DetailTitle>
          <JobHowToApplyIcon
            fill={colors.blackHigh}
            title={howToApplyLabel}
          />
          <StyledSubtitle>
            {howToApplyLabel}
            {': '}
          </StyledSubtitle>
        </DetailTitle>
        <DetailData>
          <StyledSubtitle>
            {howToApply}
          </StyledSubtitle>
        </DetailData>
      </DetailsContainer>
    </GridCell>
  </JobInfoGrid>
);

JobInfoTable.propTypes = jobInfoTablePropertyTypes;

export default JobInfoTable;
